import * as React from "react";
import { useState } from "react";
import ModalPopup from "../commons/components/modal/ModalPopup";
import { Info } from "../commons/components/Alerts";
import Form from "../commons/components/form/Form";
import "./FlowsModal.scss";
import FlowType from "../commons/model/FlowType";
import FlowEdition from "./components/FlowEdition";
import { Flow } from "./model/Flow";
import Error from "../commons/components/Error";
import TextAction from "../commons/components/action/TextAction";
import AsyncAction from "../AsyncAction";
import { createRoot } from "react-dom/client";

interface IntegrationFlowsModalProps {
  onModalClose: () => void;
  onSave: (flows: Flow[]) => void;
  initialFlows: Flow[];
}

export default function FlowsModal({
  onModalClose,
  onSave,
  initialFlows,
}: IntegrationFlowsModalProps) {
  const [flows, setFlows] = useState<Flow[]>(initialFlows);
  const [flowsDeletionToConfirm, setFlowsDeletionToConfirm] = useState<number[]>([]);

  const showDuplicatedFlowTypesError =
    flows.filter((flow) => flow.type === FlowType.FULL_ASSESSMENT).length > 1 ||
    flows.filter((flow) => flow.type === FlowType.SMILE_ASSESSMENT).length > 1 ||
    flows.filter((flow) => flow.type === FlowType.SMILE_SIMULATION).length > 1;

  const submit = (): Promise<any> => {
    onSave(flows);
    return Promise.resolve();
  };

  const getNextFlowType = () => {
    if (!flows.find((flow) => flow.type === FlowType.FULL_ASSESSMENT)) {
      return FlowType.FULL_ASSESSMENT;
    }
    if (!flows.find((flow) => flow.type === FlowType.SMILE_ASSESSMENT)) {
      return FlowType.SMILE_ASSESSMENT;
    }
    return FlowType.SMILE_SIMULATION;
  };

  const addFlow = () => {
    setFlows([
      ...flows,
      {
        id: Math.random().toString(),
        name: "",
        type: getNextFlowType(),
      },
    ]);
  };

  const removeFlow = (index: number) => {
    hideRemoveFlowConfirm(index);
    setFlows(flows.filter((_, i) => i !== index));
  };

  const showRemoveFlowConfirm = (index: number) => {
    setFlowsDeletionToConfirm([...flowsDeletionToConfirm, index]);
  };

  const hideRemoveFlowConfirm = (index: number) => {
    setFlowsDeletionToConfirm(flowsDeletionToConfirm.filter((i) => i !== index));
  };

  const onFlowChange = (index: number, newFlow: Flow) => {
    setFlows(flows.map((flow, i) => (i === index ? newFlow : flow)));
  };

  const shouldDisableSave = () => {
    return flows.find((flow) => !flow.name || flow.name.trim().length === 0) !== undefined;
  };

  return (
    <ModalPopup closeModal={onModalClose} id="modal-flow-label">
      <div className="title">
        {i18n["Page.Settings.Customisation.Flow.Integration.Modal.Title"]}
      </div>
      <p>{i18n["Page.Settings.Customisation.Flow.Integration.Modal.Subtitle"]}</p>
      {!showDuplicatedFlowTypesError && (
        <Info>{i18n["Page.Settings.Customisation.Flow.Integration.Modal.Indications"]}</Info>
      )}
      {showDuplicatedFlowTypesError && (
        <Error errorCode="Page.Settings.Customisation.Flow.Integration.Modal.SameSettings.Error" />
      )}
      <Form
        submitLabel={i18n["Page.Settings.Customisation.Flow.Integration.Modal.Submit.Button"]}
        submit={submit}
        submitDisabled={shouldDisableSave}
      >
        {flows.map((flow, index) => (
          <div
            className={`v-flex ${
              flowsDeletionToConfirm.includes(index) ? "is-delete-confirm" : ""
            }`}
            key={flow.id}
          >
            <div className="flex-center flow-line">
              <FlowEdition
                isError={flows.filter((f) => f.type === flow.type).length > 1}
                initialFlow={flow}
                onFlowChange={(newFlow) => onFlowChange(index, newFlow)}
              ></FlowEdition>
              <i
                className={`ic_trash team-edition ${flows.length === 1 ? "disabled" : "clickable"}`}
                onClick={() => showRemoveFlowConfirm(index)}
              />
            </div>
            {flowsDeletionToConfirm.includes(index) && (
              <div className="confirm-delete-section v-flex">
                <div className="confirm-delete-panel">
                  <div className="v-flex">
                    <span className="confirm-delete-title">
                      {
                        i18n[
                          "Page.Settings.Customisation.Flow.Integration.Modal.Flow.Delete.Message.Title"
                        ]
                      }
                    </span>
                    <span className="confirm-delete-warning">
                      {
                        i18n[
                          "Page.Settings.Customisation.Flow.Integration.Modal.Flow.Delete.Message"
                        ]
                      }
                    </span>
                  </div>
                  <AsyncAction
                    promise={() => {
                      removeFlow(index);
                      return Promise.resolve();
                    }}
                    buttonLabel={
                      i18n["Page.Settings.Customisation.Flow.Integration.Modal.Flow.Delete.Confirm"]
                    }
                    buttonType="button-primary danger"
                  />
                  <TextAction
                    label={i18n["Global.Cancel"]}
                    onClick={() => hideRemoveFlowConfirm(index)}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
        {flows.length < 3 && (
          <button type="button" className="plus-button clickable" onClick={addFlow}>
            <i className="ic_plus" />
          </button>
        )}
      </Form>
    </ModalPopup>
  );
}

FlowsModal.loadComponent = (elementId: string, props: IntegrationFlowsModalProps) => {
  const container = document.getElementById(elementId);
  const root = createRoot(container!);
  root.render(<FlowsModal {...props} />);
};
